'use client';

import React, { useEffect, useState } from 'react';
import { webInteractionRecorder } from './web-interaction-recorder';
import { isMobileRecordingDevice } from './web-recording-media-helpers';
import type { RecordingStatus } from '../tour-recording/tour-recording-types';

interface AgentRecordButtonProps {
    size?: number;
    forceVisible?: boolean;
}

export default function AgentRecordButton({ size = 48, forceVisible = false }: AgentRecordButtonProps) {
    const [status, setStatus] = useState<RecordingStatus>(() => webInteractionRecorder.getStatus());
    const [elapsed, setElapsed] = useState<number>(() => webInteractionRecorder.getElapsedTime());
    const [isMobile, setIsMobile] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        return isMobileRecordingDevice();
    });

    useEffect(() => {
        const checkMobile = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(isMobileRecordingDevice());
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);


    useEffect(() => {
        const handleStatus = (e: any) => {
            if (e?.detail?.recorder === 'web_interaction' && e?.detail?.status) {
                setStatus(e.detail.status);
            } else {
                setStatus(webInteractionRecorder.getStatus());
            }
        };

        const handleTime = (e: any) => {
            if (e?.detail?.recorder === 'web_interaction' && typeof e?.detail?.elapsedTime === 'number') {
                setElapsed(e.detail.elapsedTime);
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('tour-recording-status-change', handleStatus);
            window.addEventListener('tour-recording-time-update', handleTime);
        }

        const interval = setInterval(() => {
            const currentStatus = webInteractionRecorder.getStatus();
            setStatus(currentStatus);
            if (currentStatus === 'recording') {
                setElapsed(webInteractionRecorder.getElapsedTime());
            }
        }, 400);

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('tour-recording-status-change', handleStatus);
                window.removeEventListener('tour-recording-time-update', handleTime);
            }
            clearInterval(interval);
        };
    }, []);

    const isRecording = status === 'recording';
    const isBusy = status === 'stopping' || status === 'uploading';
    // Ẩn hoàn toàn trên thiết bị di động (Mobile) để tránh bấm nhầm và xung đột tài nguyên
    if (isMobile && !forceVisible) {
        return null;
    }


    const handleClick = () => {
        if (isBusy) return;
        if (isRecording) {
            webInteractionRecorder.stopRecording();
        } else if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('open-web-recording-mic-modal'));
        }
    };

    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    const formattedTime = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    const iconSize = Math.round(size * 0.46);
    const stopSquareSize = Math.round(size * 0.36);

    return (
        <div className="hidden md:flex" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {isRecording && (
                <div
                    style={{
                        position: 'absolute',
                        top: '-26px',
                        padding: '2px 7px',
                        borderRadius: '9999px',
                        backgroundColor: 'rgba(220, 38, 38, 0.95)',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.35)',
                        pointerEvents: 'none'
                    }}
                >
                    ● {formattedTime}
                </div>
            )}

            <button
                type="button"
                data-testid="agent-web-record-button"
                onClick={handleClick}
                disabled={isBusy}
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '50%',
                    backgroundColor: isRecording
                        ? '#dc2626'
                        : isBusy
                        ? '#475569'
                        : 'rgba(15, 23, 42, 0.88)',
                    color: 'white',
                    border: isRecording
                        ? '2px solid rgba(254, 202, 202, 0.9)'
                        : '2px solid rgba(255, 255, 255, 0.25)',
                    cursor: isBusy ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isRecording
                        ? '0 0 20px rgba(239, 68, 68, 0.7)'
                        : '0 10px 15px -3px rgba(0, 0, 0, 0.35)',
                    transition: 'all 0.3s ease',
                    opacity: isBusy ? 0.75 : 1,
                    padding: 0
                }}
                title={
                    isRecording
                        ? `Đang ghi hình tương tác AI (${formattedTime}) - Nhấn để dừng & lưu video`
                        : isBusy
                        ? 'Đang xử lý & lưu video...'
                        : 'Ghi hình tương tác AI & trang web'
                }
            >
                {isBusy ? (
                    <svg
                        className="animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                ) : isRecording ? (
                    <div
                        style={{
                            width: `${stopSquareSize}px`,
                            height: `${stopSquareSize}px`,
                            borderRadius: '4px',
                            backgroundColor: 'white'
                        }}
                    />
                ) : (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                        </svg>
                        <span
                            style={{
                                position: 'absolute',
                                top: '-2px',
                                right: '-2px',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#ef4444',
                                border: '1.5px solid white'
                            }}
                        />
                    </div>
                )}
            </button>
        </div>
    );
}
